import { FLOWS } from 'universal/ucs'
import { FLOW_STEPS } from '@/constants/flow-steps'

const flowName = FLOWS.MANAGE_CARD
const { CARD_DETAILS } = FLOW_STEPS.MANAGE_CARD

// Card Details Page
const cardDetailsPageTag = `${flowName}:${CARD_DETAILS}`

/**
 * Enum for Card Details FPTI event names in Card Management.
 * @enum {string}
 */
const CardDetailsEvents = {
  /** Card details screen is shown. */
  CARD_DETAILS_SHOWN: 'ucs_detail_screen_shown',
  /** Card widget is shown on card details page. */
  CARD_WIDGET_SHOWN: 'ucs_detail_card_module_shown',
  /** Card action status widget is shown on card details page. */
  CARD_ACTION_STATUS_WIDGET_SHOWN: 'ucs_detail_status_module_shown',
  /** Card action status screen is shown. */
  CARD_ACTION_STATUS_SHOWN: 'ucs_card_status_screen_shown',
  /** Activate button is pressed on card action status screen. */
  CARD_ACTION_STATUS_ACTIVATE_CLICKED: 'ucs_card_status_activate_pressed',
  /** Did not receive card button is pressed on card action status screen. */
  CARD_ACTION_STATUS_DID_NOT_RECEIVE_CLICKED: 'ucs_card_status_did_not_receive_card_pressed',
  /** Dismiss button is pressed on card action status screen. */
  CARD_ACTION_STATUS_DISMISS_CLICKED: 'ucs_card_status_dismiss_pressed',
  /** Show card button is pressed on card details page. */
  SHOW_CARD_CLICKED: 'ucs_detail_card_instant_access_pressed',
  /** Add money button is pressed on card details page. */
  ADD_MONEY_CLICKED: 'ucs_detail_add_money_pressed'
};

const CARD_DETAILS_TAGS = {
  cardDetailImpressionTags: {
    page: cardDetailsPageTag,
    eventName: CardDetailsEvents.CARD_DETAILS_SHOWN,
  },
  cardWidgetImpressionTags: {
    page: cardDetailsPageTag,
    eventName: CardDetailsEvents.CARD_WIDGET_SHOWN,
  },
  cardActionStatusWidgetImpressionTags: {
    page: cardDetailsPageTag,
    eventName: CardDetailsEvents.CARD_ACTION_STATUS_WIDGET_SHOWN,
  },
  cardActionStatusImpressionTags: {
    eventName: CardDetailsEvents.CARD_ACTION_STATUS_SHOWN,
  },
  cardActionStatusActivateClickTags: {
    eventName: CardDetailsEvents.CARD_ACTION_STATUS_ACTIVATE_CLICKED,
  },
  cardActionStatusDidNotReceiveCardClickTags: {
    eventName: CardDetailsEvents.CARD_ACTION_STATUS_DID_NOT_RECEIVE_CLICKED,
  },
  cardActionStatusDismissClickTags: {
    eventName: CardDetailsEvents.CARD_ACTION_STATUS_DISMISS_CLICKED,
  },
  showCardClickTags: {
    eventName: CardDetailsEvents.SHOW_CARD_CLICKED,
    link: 'showCardBtn',
  },
  addMoneyClickTags: {
    eventName: CardDetailsEvents.ADD_MONEY_CLICKED,
    link: 'addMoneyClick',
  },
};

/**
 * Enum for Engagement Card FPTI event names.
 * @enum {string}
 */
const EngagementCardEvents = {
  /** Engagement tile error occurred on card details page. */
  ENGAGEMENT_TILE_ERROR: 'ucs_spf_engagement_tile_error_occurred',
  /** Engagement tile not shown on card details page. */
  ENGAGEMENT_TILE_NOT_SHOWN: 'ucs_spf_engagement_tile_not_shown'
}

const ENGAGEMENT_CARD_TAGS = {
  engagementCardErrorTags: {
    page: cardDetailsPageTag,
    eventName: EngagementCardEvents.ENGAGEMENT_TILE_ERROR,
  },
  engagementCardNotShownTags: {
    page: cardDetailsPageTag,
    eventName: EngagementCardEvents.ENGAGEMENT_TILE_NOT_SHOWN,
  },
};

/**
 * Enum for Automatic Top Up FPTI event names.
 * @enum {string}
 */
const AutomaticTopUpEvents = {
  /** Automatic top up details shown. */
  AUTOMATIC_TOP_UP_DETAILS_SHOWN: 'ucs_detail_manage_automatic_top_up_details_shown',
  /** Automatic top up details hidden. */
  AUTOMATIC_TOP_UP_DETAILS_HIDDEN: 'ucs_detail_manage_automatic_top_up_details_hidden',
  /** Automatic top up details hidden for US. */
  AUTOMATIC_TOP_UP_DETAILS_HIDDEN_US: 'ucs_detail_manage_automatic_top_up_details_hidden_for_us',
  /** Edit funding source link pressed for automatic top up. */
  EDIT_FUNDING_SOURCE_LINK_CLICKED: 'ucs_detail_manage_edit_funding_source_link_pressed',
  /** Automatic top up error occurred. */
  AUTOMATIC_TOP_UP_ERROR: 'ucs_detail_manage_automatic_top_up_error_occurred',
}
const AUTOMATIC_TOP_UP_TAGS = {
  automaticTopUpShownImpressionTags: {
    page: cardDetailsPageTag,
    eventName: AutomaticTopUpEvents.AUTOMATIC_TOP_UP_DETAILS_SHOWN,
    e: 'im', // Keep existing properties unchanged
  },
  automaticTopUpHiddenImpressionTags: {
    page: cardDetailsPageTag,
    eventName: AutomaticTopUpEvents.AUTOMATIC_TOP_UP_DETAILS_HIDDEN,
    e: 'im', // Keep existing properties unchanged
  },
  automaticTopUpHiddenUSImpressionTags: {
    page: cardDetailsPageTag,
    eventName: AutomaticTopUpEvents.AUTOMATIC_TOP_UP_DETAILS_HIDDEN_US,
    e: 'im', // Keep existing properties unchanged
  },
  editFundingSourceLinkClickTags: {
    link: 'changeLink',
    eventName: AutomaticTopUpEvents.EDIT_FUNDING_SOURCE_LINK_CLICKED,
  },
  automaticTopUpErrorTags: {
    page: cardDetailsPageTag,
    eventName: AutomaticTopUpEvents.AUTOMATIC_TOP_UP_ERROR,
  },
};

/**
 * Enum for Activity Panel FPTI event names.
 * @enum {string}
 */
const ActivityPanelEvents = {
  /** Activity panel is shown. */
  ACTIVITY_PANEL_SHOWN: 'ucs_detail_activity_module_shown',
  /** Activity retry message shown. */
  ACTIVITY_RETRY_SHOWN: 'ucs_activity_try_again_shown',
  /** Activity retry button pressed. */
  ACTIVITY_RETRY_PRESSED: 'ucs_activity_try_again_pressed',
  /** Activity retry later message shown. */
  ACTIVITY_RETRY_LATER_SHOWN: 'ucs_activity_try_again_later_shown',
}
const ACTIVITY_PANEL_TAGS = {
  activityPanelImpressionTags: {
    eventName: ActivityPanelEvents.ACTIVITY_PANEL_SHOWN,
  },
  activityRetryTags: {
    page: cardDetailsPageTag,
    eventName: ActivityPanelEvents.ACTIVITY_RETRY_SHOWN,
  },
  activityRetryClickTags: {
    eventName: ActivityPanelEvents.ACTIVITY_RETRY_PRESSED,
    link: 'tryAgainBtn',
  },
  activityRetryErrorTags: {
    page: cardDetailsPageTag,
    eventName: ActivityPanelEvents.ACTIVITY_RETRY_LATER_SHOWN,
  },
};

/**
 * Enum for Card Management links FPTI event names.
 * @enum {string}
 */
const LinksEvents = {
  /** Links section is shown. */
  LINKS_SHOWN: 'ucs_detail_manage_links_shown',
  /** Help center link pressed. */
  HELP_CENTER_LINK_PRESSED: 'ucs_detail_manage_help_center_pressed',
  /** Learn more (fees) link pressed. */
  LEARN_MORE_PRESSED: 'ucs_detail_manage_learn_more_pressed',
}
const LINKS_TAGS = {
  linksImpressionTags: {
    page: cardDetailsPageTag,
    eventName: LinksEvents.LINKS_SHOWN,
  },
  helpCenterLinkClickTags: {
    eventName: LinksEvents.HELP_CENTER_LINK_PRESSED,
    link: 'helpCenterLink',
  },
  feesLinkClickTags: {
    eventName: LinksEvents.LEARN_MORE_PRESSED,
    link: 'feesLink',
  },
};

/**
 * Enum for Manage Panel FPTI event names.
 * @enum {string}
 */
const ManagePanelEvents = {
  /** Manage panel is shown. */
  MANAGE_PANEL_SHOWN: 'ucs_detail_manage_module_shown',
  /** Lock/unlock card toggle shown. */
  LOCK_UNLOCK_TOGGLE_SHOWN: 'ucs_detail_manage_lock_card_toggle_shown',
  /** Lock/unlock card toggle pressed. */
  LOCK_UNLOCK_TOGGLE_PRESSED: 'ucs_detail_manage_lock_card_toggle_pressed',
  /** Change PIN link shown. */
  CHANGE_PIN_SHOWN: 'ucs_detail_manage_change_pin_shown',
  /** Change PIN link pressed. */
  CHANGE_PIN_PRESSED: 'ucs_detail_manage_change_pin_pressed',
  /** Add cash link shown. */
  ADD_CASH_SHOWN: 'ucs_detail_manage_add_cash_shown',
  /** Add cash link pressed. */
  ADD_CASH_PRESSED: 'ucs_detail_manage_add_cash_pressed',
  /** Find ATM link shown. */
  FIND_ATM_SHOWN: 'ucs_detail_manage_find_atm_shown',
  /** Find ATM link pressed. */
  FIND_ATM_PRESSED: 'ucs_detail_manage_find_atm_pressed',
  /** Replace card link shown. */
  REPLACE_CARD_SHOWN: 'ucs_detail_manage_report_shown',
  /** Replace card link pressed. */
  REPLACE_CARD_PRESSED: 'ucs_detail_manage_report_pressed',
  /** Replace card digitally link shown. */
  REPLACE_CARD_DIGITAL_SHOWN: 'ucs_detail_manage_report_digital_shown',
  /** Replace card digitally link pressed. */
  REPLACE_CARD_DIGITAL_PRESSED: 'ucs_detail_manage_report_digital_pressed',
  /** Get physical card link shown. */
  GET_PHYSICAL_CARD_SHOWN: 'ucs_detail_manage_get_physical_card_shown',
  /** Get physical card link pressed. */
  GET_PHYSICAL_CARD_PRESSED: 'ucs_detail_manage_get_physical_card_pressed',
  /** Get physical card with fee link shown. */
  GET_PHYSICAL_CARD_WITH_FEE_SHOWN: 'ucs_detail_manage_get_physical_card_with_fee_shown',
  /** Get physical card with fee link pressed. */
  GET_PHYSICAL_CARD_WITH_FEE_PRESSED: 'ucs_detail_manage_get_physical_card_with_fee_pressed',
  /** Cancel card link shown. */
  CANCEL_CARD_SHOWN: 'ucs_detail_manage_cancel_card_shown',
  /** Cancel card link pressed. */
  CANCEL_CARD_PRESSED: 'ucs_detail_manage_cancel_card_pressed',
}
const MANAGE_PANEL_TAGS = {
  managePanelImpressionTags: {
    page: cardDetailsPageTag,
    eventName: ManagePanelEvents.MANAGE_PANEL_SHOWN,
  },
  lockOrUnlockCardImpressionTags: {
    eventName: ManagePanelEvents.LOCK_UNLOCK_TOGGLE_SHOWN,
  },
  lockCardClickTags: {
    eventName: ManagePanelEvents.LOCK_UNLOCK_TOGGLE_PRESSED,
    link: 'lockOrUnlockCardClick',
    action: 'lock',
  },
  unlockCardClickTags: {
    eventName: ManagePanelEvents.LOCK_UNLOCK_TOGGLE_PRESSED,
    link: 'lockOrUnlockCardClick',
    action: 'unlock',
  },
  changePinImpressionTags: {
    eventName: ManagePanelEvents.CHANGE_PIN_SHOWN,
  },
  changePinClickTags: {
    eventName: ManagePanelEvents.CHANGE_PIN_PRESSED,
    link: 'changePinClick',
  },
  addCashImpressionTags: {
    eventName: ManagePanelEvents.ADD_CASH_SHOWN,
  },
  addCashClickTags: {
    eventName: ManagePanelEvents.ADD_CASH_PRESSED,
    link: 'addCashClick',
  },
  findAtmImpressionTags: {
    eventName: ManagePanelEvents.FIND_ATM_SHOWN,
  },
  findAtmClickTags: {
    eventName: ManagePanelEvents.FIND_ATM_PRESSED,
    link: 'findAtmClick',
  },
  replaceCardImpressionTags: {
    eventName: ManagePanelEvents.REPLACE_CARD_SHOWN,
  },
  replaceCardClickTags: {
    eventName: ManagePanelEvents.REPLACE_CARD_PRESSED,
    link: 'replaceCardClick',
  },
  replaceDigitalCardImpressionTags: {
    eventName: ManagePanelEvents.REPLACE_CARD_DIGITAL_SHOWN,
  },
  replaceDigitalCardClickTags: {
    eventName: ManagePanelEvents.REPLACE_CARD_DIGITAL_PRESSED,
    link: 'replaceDigitalCardClick',
  },
  getPhysicalCardImpressionTags: {
    eventName: ManagePanelEvents.GET_PHYSICAL_CARD_SHOWN,
  },
  getPhysicalCardClickTags: {
    eventName: ManagePanelEvents.GET_PHYSICAL_CARD_PRESSED,
    link: 'getPhysicalCardClick',
  },
  getPhysicalCardWithFeeImpressionTags: {
    eventName: ManagePanelEvents.GET_PHYSICAL_CARD_WITH_FEE_SHOWN,
  },
  getPhysicalCardWithFeeClickTags: {
    eventName: ManagePanelEvents.GET_PHYSICAL_CARD_WITH_FEE_PRESSED,
    link: 'getPhysicalCardWithFeeClick',
  },
  cancelCardImpressionTags: {
    eventName: ManagePanelEvents.CANCEL_CARD_SHOWN,
  },
  cancelCardClickTags: {
    eventName: ManagePanelEvents.CANCEL_CARD_PRESSED,
    link: 'cancelCardClick',
  },
  viewPinImpressionTags: {
    eventName: 'ucs_detail_manage_view_pin_shown',
  },
  viewPinClickTags: {
    eventName: 'ucs_detail_manage_view_pin_pressed',
    link: 'viewPinClick',
  },
}

/**
 * Enum for Daily Limits FPTI event names.
 * @enum {string}
 */
const DailyLimitsEvents = {
  /** Daily limits section shown. */
  DAILY_LIMITS_SHOWN: 'ucs_detail_manage_daily_limits_shown',
}
const DAILY_LIMITS_TAGS = {
  dailyLimitsImpressionTags: {
    page: cardDetailsPageTag,
    eventName: DailyLimitsEvents.DAILY_LIMITS_SHOWN,
  },
};


/**
 * Enum for Billing Address FPTI event names.
 * @enum {string}
 */
const BillingAddressEvents = {
  /** Billing address shown. */
  BILLING_ADDRESS_SHOWN: 'ucs_detail_manage_billing_address_shown',
  /** Billing address pressed. */
  BILLING_ADDRESS_PRESSED: 'ucs_detail_manage_billing_address_pressed',
}
const BILLING_ADDRESS_TAGS = {
  billingAddressImpressionTags: {
    page: cardDetailsPageTag,
    eventName: BillingAddressEvents.BILLING_ADDRESS_SHOWN,
  },
  updateBillingAddressClickTags: {
    eventName: BillingAddressEvents.BILLING_ADDRESS_PRESSED,
    link: 'updateBillingAddressClick',
  },
};

/**
 * Enum for Funded By FPTI event names.
 * @enum {string}
 */
const FundedByEvents = {
  /** Funding source widget shown. */
  FUNDING_SOURCE_SHOWN: 'ucs_fi_widget_shown',
  /** Change funding source pressed. */
  CHANGE_FUNDING_SOURCE_PRESSED: 'ucs_detail_manage_fi_pressed',
  /** Link a bank pressed in funding source widget. */
  LINK_BANK_PRESSED: 'ucs_fi_widget_link_a_bank_pressed',
  /** Funding source widget error occurred. */
  FUNDING_SOURCE_ERROR: 'ucs_fi_widget_error_occurred',
  /** Funding source widget error shown. */
  FUNDING_SOURCE_ERROR_SHOWN: 'ucs_fi_widget_error_shown',
  /** Retry button pressed in funding source widget. */
  FUNDING_SOURCE_RETRY_PRESSED: 'ucs_fi_widget_retry_pressed',
  /** No bank linked shown in funding source widget. */
  NO_BANK_LINKED_SHOWN: 'ucs_fi_widget_no_bank_shown',
  /** Bank linked shown in funding source widget. */
  BANK_LINKED_SHOWN: 'ucs_fi_widget_with_bank_shown',
}
const FUNDED_BY_TAGS = {
  fundedByImpressionTags: {
    page: cardDetailsPageTag,
    eventName: FundedByEvents.FUNDING_SOURCE_SHOWN,
  },
  fundedByChangeClickTags: {
    page: cardDetailsPageTag,
    eventName: FundedByEvents.CHANGE_FUNDING_SOURCE_PRESSED,
    link: 'changeFundingLink',
  },
  fundedByAddBankClickTags: {
    page: cardDetailsPageTag,
    eventName: FundedByEvents.LINK_BANK_PRESSED,
    link: 'addBankLink',
  },
  fundedByErrorActivityTags: {
    eventName: FundedByEvents.FUNDING_SOURCE_ERROR,
  },
  fundedByRetryTags: {
    page: cardDetailsPageTag,
    eventName: FundedByEvents.FUNDING_SOURCE_ERROR_SHOWN,
  },
  fundedByRetryClickTags: {
    eventName: FundedByEvents.FUNDING_SOURCE_RETRY_PRESSED,
    link: 'tryAgainBtn',
  },
  fundedByNoBankShownImpressionTags: {
    eventName: FundedByEvents.NO_BANK_LINKED_SHOWN,
  },
  fundedByBankShownImpressionTags: {
    eventName: FundedByEvents.BANK_LINKED_SHOWN,
  },
};

/**
 * Enum for Notification FPTI event names.
 * @enum {string}
 */
const NotificationEvents = {
  /** Notification choice shown. */
  NOTIFICATION_CHOICE_SHOWN: 'ucs_detail_notification_choice_shown',
  /** Email notification toggle pressed. */
  EMAIL_NOTIFICATION_TOGGLE_PRESSED: 'ucs_detail_email_notification_toggle_pressed',
  /** Notification banner shown. */
  NOTIFICATION_BANNER_SHOWN: 'ucs_banner_widget_shown',
  /** Notification banner pressed. */
  NOTIFICATION_BANNER_PRESSED: 'ucs_banner_widget_pressed',
}
const NOTIFICATION_TAGS = {
  notificationImpressionTags: {
    page: cardDetailsPageTag,
    eventName: NotificationEvents.NOTIFICATION_CHOICE_SHOWN,
  },
  notificationToggleTags: (checked) => {
    return {
      page: cardDetailsPageTag,
      eventName: NotificationEvents.EMAIL_NOTIFICATION_TOGGLE_PRESSED,
      optionSelected: checked ? 'on' : 'off',
    };
  },
  notificationBannerImpressionTags: {
    page: cardDetailsPageTag,
    eventName: NotificationEvents.NOTIFICATION_BANNER_SHOWN,
  },
  notificationBannerClickTags: {
    eventName: NotificationEvents.NOTIFICATION_BANNER_PRESSED,
    link: 'bannerClick',
  },
};

const MANAGE_CARD_TAGS = {
  ...CARD_DETAILS_TAGS,
  ...ENGAGEMENT_CARD_TAGS,
  ...AUTOMATIC_TOP_UP_TAGS,
  ...ACTIVITY_PANEL_TAGS,
  ...MANAGE_PANEL_TAGS,
  ...LINKS_TAGS,
  ...DAILY_LIMITS_TAGS,
  ...BILLING_ADDRESS_TAGS,
  ...FUNDED_BY_TAGS,
  ...NOTIFICATION_TAGS,
}

export default MANAGE_CARD_TAGS
